import { useQueryClient } from '@tanstack/react-query';
import { useContext, useEffect, useState } from 'react';
import InfoMessage from '../../components/InfoMessage';
import Input from '../../components/Input';
import Modal from '../../components/Modal';
import { QCContext, UserContext } from '../../context/UserContext';
import BarcodeScanner from '../../helpers/BarcodeScanner';
import { addPanelToBin, getPanelList, getQCRequests, raiseQCRequest } from '../../services/qcService';
import ScanCard from './ScanCard';
import styles from "./ScanView.module.css";

function ScanView() {
  const {workOrder, toastList, setToastList, selectedLocation, currentFactory, setShowLoadingFull} = useContext(UserContext);
  const [addManully, setAddManually] = useState(false);
  const [boardScanData, setBoardScanData] = useState(undefined);
  const [barcode, setBarcode] = useState('');
  const {selectedPO, woPanelList} = useContext(QCContext);
  const [showQCRN, setShowQCRN] = useState(false);
  const queryClient = useQueryClient()

  useEffect(() => {
    initializeBarCodeListener();
    return () => removeBarCodeListener()
  }, [woPanelList])

  useEffect(() => {
    setBoardScanData(undefined)
  }, [workOrder])

  const handleScanListener = (event) => {
    onBarcodeScan(event.detail);
  }

  const initializeBarCodeListener = () => {
    BarcodeScanner.initialize();
    document.addEventListener('onbarcodescanned', handleScanListener)
  }

  const removeBarCodeListener = () => {
    document.removeEventListener('onbarcodescanned', handleScanListener)
    BarcodeScanner.close()
  }

  useEffect(() => {
    if (selectedPO && selectedPO !== '') {
      setShowQCRN(false);
      showPanelDetails(selectedPO).then(async (response) => {
        if (!response) {
          return;
        }
        let allPanelScanned = true;
        response.parts.map((panel, index) => {
          if (!panel.status || panel.status === 'rejected') {
            allPanelScanned = false;
            return;
          }
        })
        if (allPanelScanned) {
          let showRetry = true;
          let qcList = await getQCRequestsForPO(selectedPO);
          if (qcList && qcList.length > 0) {
            qcList.map((qcrequest) => {
              if (qcrequest.status.toLowerCase() === 'pending' || qcrequest.status.toLowerCase() === 'rework' || qcrequest.status.toLowerCase() === 'completed') {
                showRetry = false;
                return;
              }
            })
          } else {
            showRetry = true;
          }
          if (showRetry) {
            setShowQCRN(true);
          }
        }
      });
      window.scrollTo(0, 0)
    }
  }, [selectedPO])

  const onBarcodeScan = async(barcode) => {
    setShowLoadingFull(true);
    try {
      if (woPanelList && woPanelList[barcode.split('_')[0]] === undefined && Object.values(woPanelList).indexOf(barcode.split('PO')[0]) === -1) {
        // validation to check whether panel belongs to the same work order
        // validating both panel format - ex: (MAI0078834_10161, 230318189PO10104)
        showToast({type: 'error', message: "Scanned panel doesn't belong this Work Order", autoClose: false});
        setShowLoadingFull(false);
        return;
      }
      const scannedPanel = await addPanelToBin({
        factory_code: currentFactory.item_code,
        panel_code: barcode.trim().toUpperCase(),
        location: selectedLocation.location
      })
      if (scannedPanel.status !== 200) {
        showToast({type: 'error', message: scannedPanel.message || 'Internal server error. Kindly try again!', autoClose: false});
      } else {
        const response = await showPanelDetails(scannedPanel.message.po_code);
        if (boardScanData?.parts?.length > 0 && scannedPanel.message.po_code !== boardScanData.parts[0].po_code) {
          showToast({type: 'info', message: `Panel (${barcode.trim()}) scanned successfully`, autoClose: false})
        } else {
          showToast({type: 'success', message: `Panel (${barcode.trim()}) scanned successfully`, autoClose: false})
        }
        if (response && response.parts.filter(part => part.status !== 'rejected' && part.status !== '').length === response.total_parts_for_po) {
          requestQCNumber(scannedPanel.message.po_code)
        }
        queryClient.invalidateQueries({ queryKey: ['panelList', scannedPanel.message.po_code] })
      }
    } catch (err) {
      showToast({type: 'error', message: err.message || err, autoClose: false});
    }
    setShowLoadingFull(false);
  }

  const showPanelDetails = async (po_code) => {
    let payload = {
      factory_code: currentFactory.item_code,
      location: selectedLocation.location,
      po_code: po_code,
      sorted_panels: false
    }
    const response = await getPanelList(payload);
    if (response.status !== 200) {
      showToast({type: 'error', message: response.message || 'Internal server error. Kindly try again!', autoClose: false});
    } else {
      let sortingArr = ['rejected', 'pending', 'approved'];
      response.message.parts.sort((a, b) => sortingArr.indexOf(a.status) - sortingArr.indexOf(b.status));
      setBoardScanData(response.message);
      return response.message;
    }
  }

  const showToast = ({type, message, action, autoClose = true}) => {
    // show toast message
    let toast = {
      type: type,
      text: message,
      onClick: action ? () => console.log('button clicked') : undefined,
      autoClose: autoClose
    }
    setToastList([toast]);
  }

  const addBarCodeManul = () => {
    if (barcode.length > 5) {
      onBarcodeScan(barcode);
      // setBoardScanData({})
      setBarcode('');
      setAddManually(false);
    }
  }

  const requestQCNumber = async (po_code) => {
    const qcRaise = await raiseQCRequest({
      factory_code: currentFactory.item_code,
      po_code
    });
    if (qcRaise.status !== 200) {
      showToast({type: 'error', message: qcRaise.message || 'Internal server error. Kindly try again!', autoClose: false});
    } else {
      showToast({type: 'success', message: `QC request number generated (${qcRaise.message.request_id})`, autoClose: false});
      setShowQCRN(false);
    }
  }

  const getQCRequestsForPO = async (po_code) => {
    const qcList = await getQCRequests({
      po_code,
      factory_code: currentFactory.item_code
    })
    if (qcList.status === 200) {
      return qcList.message;
    }
  }

  return (
    <div className={styles.scanView}>
      <div className={styles.spaceBetween}>
        <span className="bold">Scan Details</span>
        {/* <div>
          {!boardScanData && <Button type={'secondary'} fillStyle={'outline'} onClick={() => setAddManually(true)}>
            <MaterialIconsReact icon={'add'} size={20}></MaterialIconsReact>
            <span className={styles.ml8}>Add Manually</span>
          </Button>}
        </div> */}
      </div>
      {boardScanData ? <ScanCard requestQCNumber={requestQCNumber} showQCRN={showQCRN} addManually={() => setAddManually(true)} boardScanData={boardScanData}></ScanCard> : <InfoMessage message='Scan a panel to start the process'></InfoMessage>
      }
      {addManully && <Modal title={'Add panel'} onClose={() => setAddManually(false)} primaryAction={addBarCodeManul}>
        <label htmlFor="panelNumber" className={styles.inputLabel}>Enter panel number</label>
        <Input id="panelNumber" placeholder='Enter number' onChange={(e) => setBarcode(e.target.value)}></Input>
      </Modal>}
    </div>
  );
}

export default ScanView;
